import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle, Search } from 'lucide-react';
import CollegeHeader from './CollegeHeader';
import CollegeHomeSideBar from './CollegeHomeSideBar';

// --- FAQ data (unchanged) -----------------------------------------------------
const faqs = [
  { question: 'How can I update my college profile?',      answer: "Navigate to 'College Profile' > 'Edit Profile' to make changes and save." },
  { question: 'Where do I find student progress reports?', answer: "Go to 'Student' > 'Students Progress' to view individual and collective stats." },
  { question: 'How can I view top performing students?',   answer: "Under the 'Student' section, click on 'Top Performers' to view ranked results." },
  { question: 'How do I contact support?',                 answer: "Use the 'Chat Support' > 'Feedback Inbox' to reach our support team." },
  { question: 'Can I export student data?',                answer: "Yes, go to 'Total Students' and use the export button on the top right corner." },
  { question: 'Is there a dark mode available?',           answer: 'Currently, the app supports dark themes under settings > appearance.' },
  { question: 'How do I reset my password?',               answer: "Use the 'Forgot Password' option on the login screen to reset your credentials." },
  { question: 'Can I assign mentors to students?',         answer: "Yes, under each student profile, there's a 'Mentor' tab to assign faculty." },
  { question: 'What is the meaning of each student level?',answer: 'Levels indicate student progress, based on completed tasks and assessments.' },
  { question: 'How often is data updated?',                answer: 'Data is updated in real time as students interact with the platform.' },
];

// --- Design tokens ------------------------------------------------------------
const T = {
  border: '1px solid var(--border-subtle, #E2E8F0)',
  radius: { sm: '8px', md: '10px', lg: '14px', xl: '18px', full: '999px' },
  shadow: { xs: '0 1px 3px rgba(0,0,0,0.06)', sm: '0 2px 8px rgba(0,0,0,0.08)' },
};

// --- FAQ accordion item -------------------------------------------------------
const FAQItem = ({ faq, index, isOpen, onToggle }) => (
  <div style={{
    background: '#FFFFFF', border: isOpen ? '1px solid #BFDBFE' : T.border,
    borderRadius: T.radius.lg, overflow: 'hidden',
    boxShadow: isOpen ? '0 4px 16px rgba(79, 70, 229,0.08)' : T.shadow.xs,
    transition: 'border-color 0.2s, box-shadow 0.2s',
    position: 'relative',
  }}>
    {/* Accent strip - only when open */}
    {isOpen && (
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, var(--primary-blue, #4F46E5), #7C3AED)', borderRadius: `${T.radius.lg} ${T.radius.lg} 0 0` }} />
    )}

    {/* Question row */}
    <button
      onClick={() => onToggle(index)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: isOpen ? '18px 20px 14px' : '16px 20px',
        background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
        gap: 12, transition: 'padding 0.15s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1 }}>
        <div style={{
          width: 28, height: 28, borderRadius: '8px', flexShrink: 0, marginTop: 1,
          background: isOpen ? 'rgba(79, 70, 229,0.1)' : '#F8FAFC',
          border: isOpen ? '1px solid #BFDBFE' : T.border,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s',
        }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 800, color: isOpen ? 'var(--primary-blue, #4F46E5)' : '#94A3B8', fontFamily: 'monospace' }}>
            {String(index + 1).padStart(2, '0')}
          </span>
        </div>
        <span style={{
          fontSize: '0.9375rem', fontWeight: isOpen ? 700 : 600,
          color: isOpen ? 'var(--secondary-blue, #1E3A8A)' : '#0F172A', lineHeight: 1.5,
          transition: 'color 0.15s',
        }}>
          {faq.question}
        </span>
      </div>
      <div style={{
        width: 28, height: 28, borderRadius: '8px', flexShrink: 0,
        background: isOpen ? '#EFF6FF' : '#F8FAFC',
        border: isOpen ? '1px solid #BFDBFE' : T.border,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.2s',
      }}>
        {isOpen
          ? <ChevronUp  size={15} style={{ color: 'var(--primary-blue, #4F46E5)' }} />
          : <ChevronDown size={15} style={{ color: '#94A3B8' }} />}
      </div>
    </button>

    {/* Answer */}
    {isOpen && (
      <div style={{ padding: '0 20px 18px 60px' }}>
        <div style={{
          padding: '14px 16px', background: '#F8FAFC',
          border: '1px solid #E2E8F0', borderRadius: T.radius.md,
        }}>
          <p style={{ fontSize: '0.875rem', color: '#374151', lineHeight: 1.7, margin: 0 }}>
            {faq.answer}
          </p>
        </div>
      </div>
    )}
  </div>
);

// --- Main component -----------------------------------------------------------
const CollegeFAQs = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [search,    setSearch]    = useState('');

  const toggle = (index) => setOpenIndex(openIndex === index ? null : index);

  const filtered = faqs.filter(f =>
    f.question.toLowerCase().includes(search.toLowerCase()) ||
    f.answer.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="college-main-layout" style={{ background: '#F8FAFC' }}>
      <CollegeHeader />

      <div className="college-content-wrapper">
        <CollegeHomeSideBar />

        <main className="college-main-content" style={{ padding: '32px 28px' }}>
          <div style={{ maxWidth: 900, margin: '0 auto' }}>

            {/* Page header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
              <div style={{ width: 48, height: 48, borderRadius: '12px', background: 'rgba(79, 70, 229,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <HelpCircle size={22} style={{ color: 'var(--primary-blue, #4F46E5)' }} />
              </div>
              <div>
                <h1 style={{ fontSize: '1.625rem', fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em', margin: 0 }}>
                  Frequently Asked Questions
                </h1>
                <p style={{ color: '#64748B', fontSize: '0.875rem', margin: '3px 0 0' }}>
                  Find answers to common questions about the portal
                </p>
              </div>
            </div>

            {/* Search */}
            <div style={{
              background: '#FFFFFF', border: T.border, borderRadius: T.radius.lg,
              padding: '20px', marginBottom: 24, boxShadow: T.shadow.xs,
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg, var(--primary-blue, #4F46E5), #7C3AED)', borderRadius: `${T.radius.lg} ${T.radius.lg} 0 0` }} />
              <div style={{ position: 'relative', marginTop: 4 }}>
                <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', pointerEvents: 'none' }} />
                <input
                  type="text"
                  placeholder="Search questions..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 14px 10px 38px',
                    border: T.border, borderRadius: T.radius.md,
                    fontSize: '0.875rem', color: '#0F172A',
                    background: '#F8FAFC', outline: 'none', boxSizing: 'border-box',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                  onFocus={e => { e.target.style.borderColor = 'var(--primary-blue, #4F46E5)'; e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229,0.1)'; e.target.style.background = '#FFFFFF'; }}
                  onBlur={e  => { e.target.style.borderColor = 'var(--border-subtle,#E2E8F0)'; e.target.style.boxShadow = 'none'; e.target.style.background = '#F8FAFC'; }}
                />
              </div>
              {search && (
                <p style={{ margin: '10px 0 0', fontSize: '0.8rem', color: '#64748B' }}>
                  <span style={{ fontWeight: 700, color: 'var(--primary-blue, #4F46E5)' }}>{filtered.length}</span> result{filtered.length !== 1 ? 's' : ''} for "<strong>{search}</strong>"
                </p>
              )}
            </div>

            {/* Stats row */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
              {[
                { label: 'Total Questions', value: faqs.length, color: 'var(--primary-blue, #4F46E5)', bg: '#EFF6FF', border: '#BFDBFE' },
                { label: 'Showing',         value: filtered.length, color: '#16A34A', bg: '#DCFCE7', border: '#BBF7D0' },
              ].map(s => (
                <div key={s.label} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 16px', borderRadius: T.radius.full,
                  background: s.bg, border: `1px solid ${s.border}`,
                }}>
                  <span style={{ fontSize: '1rem', fontWeight: 800, color: s.color }}>{s.value}</span>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: s.color }}>{s.label}</span>
                </div>
              ))}
            </div>

            {/* FAQ list */}
            {filtered.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '64px 24px',
                background: '#FFFFFF', border: T.border, borderRadius: T.radius.lg, boxShadow: T.shadow.xs,
              }}>
                <div style={{ width: 52, height: 52, borderRadius: '14px', background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                  <HelpCircle size={24} style={{ color: '#CBD5E1' }} />
                </div>
                <p style={{ fontWeight: 700, color: '#374151', margin: '0 0 4px' }}>No results found</p>
                <p style={{ color: '#94A3B8', fontSize: '0.875rem', margin: 0 }}>Try different keywords to find your answer.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 14 }}>
                {filtered.map((faq, index) => {
                  const origIndex = faqs.indexOf(faq);
                  return (
                    <FAQItem
                      key={origIndex}
                      faq={faq}
                      index={origIndex}
                      isOpen={openIndex === origIndex}
                      onToggle={toggle}
                    />
                  );
                })}
              </div>
            )}

          </div>
        </main>
      </div>
    </div>
  );
};

export default CollegeFAQs;


